import React, { useState } from 'react';

import { FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaGlobe, FaLock, FaEye, FaEyeSlash, FaCopy, FaCheck } from 'react-icons/fa';
import { useAuth } from './context/AuthContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';

// SignupSection Component
const SignupSection = () => {
  
  const { signup } = useAuth();
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    state: '',
    country: '',
    password: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [apiError, setApiError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [suggestedPassword, setSuggestedPassword] = useState('');
  const [showPasswordSuggestion, setShowPasswordSuggestion] = useState(false);
  const [copiedSuggestion, setCopiedSuggestion] = useState(false);

  const generateSuggestedPassword = () => {
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const specials = '@$!%*?&';
    const all = `${lowercase}${uppercase}${numbers}${specials}`;

    const pick = (pool) => pool[Math.floor(Math.random() * pool.length)];
    const result = [
      pick(lowercase),
      pick(uppercase),
      pick(numbers),
      pick(specials),
    ];

    const targetLength = 12;
    while (result.length < targetLength) {
      result.push(pick(all));
    }

    // Fisher-Yates shuffle
    for (let i = result.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result.join('');
  };

  const showOrRefreshPasswordSuggestion = () => {
    setSuggestedPassword(generateSuggestedPassword());
    setShowPasswordSuggestion(true);
    setCopiedSuggestion(false);
  };

  const useSuggestedPassword = () => {
    const next = suggestedPassword || generateSuggestedPassword();
    setSuggestedPassword(next);
    setFormData((prev) => ({
      ...prev,
      password: next,
      confirmPassword: next,
    }));
    setErrors((prev) => ({
      ...prev,
      password: '',
      confirmPassword: '',
    }));
  };

  const copySuggestedPassword = async () => {
    const next = suggestedPassword || generateSuggestedPassword();
    if (!suggestedPassword) setSuggestedPassword(next);
    try {
      await navigator.clipboard.writeText(next);
      setCopiedSuggestion(true);
      setTimeout(() => setCopiedSuggestion(false), 1500);
    } catch (error) {
      console.error('Failed to copy suggested password', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear API error when user starts typing
    if (apiError) {
      setApiError('');
    }
    
    // Clear success message when user starts typing
    if (successMessage) {
      setSuccessMessage('');
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(password);
  };

  const validateForm = () => {
    const newErrors = {};

    // First name validation
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required.';
    }

    // Last name validation
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required.';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required.';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address.';
    }

    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required.';
    }

    // State validation
    if (!formData.state.trim()) {
      newErrors.state = 'State is required.';
    }

    // Country validation
    if (!formData.country.trim()) {
      newErrors.country = 'Country is required.';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required.';
    } else if (!validatePassword(formData.password)) {
      newErrors.password = 'Password must be at least 8 characters long and include uppercase, lowercase, number, and special characters.';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password.';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setApiError('');
    
    try {
      // Prepare user data for API
      const userData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        state: formData.state,
        country: formData.country,
        password: formData.password,
      };

      // console.log('Submitting signup form with data:', userData);

      const result = await signup(userData);
      
      // console.log('Signup result:', result);
      
      if (result.success) {
        setSuccessMessage('Registration successful! We have sent a verification link to your email address. Please check your inbox to activate your account and log in.');
        // Don't redirect - show the success message instead
      } else {
        setApiError(result.error);
      }
    } catch (error) {
      console.error('Signup error:', error);
      setApiError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="flex flex-grow items-center justify-center py-16 lg:py-12">
      <div className="max-w-md w-full mx-auto px-4">
        {/* Signup Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-10">
          
          {/* Header Icon */}
          <div className="flex justify-center mb-8">
            <div className="w-20 h-20 bg-gray-900 rounded-full flex items-center justify-center">
              <img src="/assets/login/user_login.png" alt="User Signup Icon" className="w-12 h-12 object-contain" />
            </div>
          </div>
          
          {/* Title */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900">
              Create Your Account
            </h2>
          </div>
          
          {/* Success Message Display */}
          {successMessage && (
            <div className="text-center">
              <div className="mb-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Registration Successful!</h3>
                <p className="text-gray-600 mb-6">
                  We have sent a verification link to your email address. Please check your inbox to activate your account and log in.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-blue-800">
                    <strong>Important:</strong> If you don't see the email, please check your spam folder.
                  </p>
                </div>
                <a 
                  href="/login" 
                  className="inline-block bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-full transition-colors duration-200"
                >
                  Go to Login
                </a>
              </div>
            </div>
          )}
          
          {/* API Error Display */}
          {apiError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{typeof apiError === 'string' ? apiError : 'An error occurred. Please try again.'}</p>
            </div>
          )}
          
          {/* Signup Form - Only show if no success message */}
          {!successMessage && (
            <form onSubmit={handleSubmit} className="space-y-4">
            
            {/* Name Input */}
            <div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaUser className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.first_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your first name"
                />
              </div>
              {errors.first_name && (
                <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>
              )}
            </div>

            {/* Last Name Input */}
            <div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaUser className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.last_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your last name"
                />
              </div>
              {errors.last_name && (
                <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>
              )}
            </div>

            {/* Email Input */}
            <div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaEnvelope className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your EMAIL ID"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Phone Input */}
            <div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaPhone className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your mobile no."
                />
              </div>
              {errors.phone && (
                <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
              )}
            </div>

            {/* State Input */}
            <div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaMapMarkerAlt className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.state ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your State"
                />
              </div>
              {errors.state && (
                <p className="mt-1 text-sm text-red-600">{errors.state}</p>
              )}
            </div>

            {/* Country Input */}
            <div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaGlobe className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.country ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your Country"
                />
              </div>
              {errors.country && (
                <p className="mt-1 text-sm text-red-600">{errors.country}</p>
              )}
            </div>

            {/* Password Input */}
            <div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaLock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  onFocus={showOrRefreshPasswordSuggestion}
                  className={`block w-full pl-10 pr-12 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <FaEyeSlash className="h-5 w-5 text-gray-400" />
                  ) : (
                    <FaEye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
              {showPasswordSuggestion && (
                <div className="mt-2 rounded-lg border border-green-200 bg-green-50 p-3">
                  <p className="text-xs text-green-800">
                    Suggested password:{' '}
                    <span className="font-semibold break-all">{suggestedPassword}</span>
                  </p>
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={copySuggestedPassword}
                      className="px-2.5 py-1.5 text-xs font-semibold rounded-md border border-green-300 text-green-700 hover:bg-green-100 inline-flex items-center gap-1"
                      title="Copy suggested password"
                    >
                      {copiedSuggestion ? <FaCheck className="h-3 w-3" /> : <FaCopy className="h-3 w-3" />}
                      {copiedSuggestion ? 'Copied' : 'Copy'}
                    </button>
                    <button
                      type="button"
                      onClick={useSuggestedPassword}
                      className="px-3 py-1.5 text-xs font-semibold rounded-md bg-green-600 text-white hover:bg-green-700"
                    >
                      Use this password
                    </button>
                    <button
                      type="button"
                      onClick={() => setSuggestedPassword(generateSuggestedPassword())}
                      className="px-3 py-1.5 text-xs font-semibold rounded-md border border-green-300 text-green-700 hover:bg-green-100"
                    >
                      Suggest another
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Confirm Password Input */}
            <div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaLock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={`block w-full pl-10 pr-12 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <FaEyeSlash className="h-5 w-5 text-gray-400" />
                  ) : (
                    <FaEye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
              )}
            </div>
            
            {/* Create Account Button */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full py-3 px-6 rounded-full font-semibold transition-colors duration-200 ${
                loading 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-green-500 hover:bg-green-600'
              } text-white`}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Creating Account...
                </div>
              ) : (
                'CREATE ACCOUNT'
              )}
            </button>
            
            {/* Sign-in Link */}
            <div className="text-center pt-4">
              <a 
                href="/login" 
                className="text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Already have an account - <span className="font-semibold text-green-600">Sign in</span>
              </a>
            </div>
          </form>
          )}
        </div>
      </div>
    </section>
  );
};

// SignupPage Component
const SignupPage = () => {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return (
    <div 
      className="min-h-screen font-karla flex flex-col"
      style={{
        backgroundImage: isMobile ? 'none' : 'url("/assets/signup/signup_bg.png")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Navbar Component */}
      <Navbar />
      
      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <SignupSection />
      </div>

      {/* Footer Component */}
      <Footer />
    </div>
  );
};

export default SignupPage;
